/**
 * SyncErrorsView — Story SCH-5.2.
 *
 * Paginated, filterable table of dedup'd SubContractorHub sync errors.
 * Reachable from a "View errors" link on the SyncStatusCard. Each row
 * shows first/last-seen, count, phase, project (if scoped), the
 * PII-normalized message, and an Acknowledge / Un-acknowledge button.
 *
 * Default filter: acknowledged=false. Toggle to "Acked" to see previously-
 * acknowledged errors (and un-acknowledge to re-surface them in default view).
 */

import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  useAcknowledgeSyncError,
  useSyncErrors,
  useUnacknowledgeSyncError,
} from '@/hooks/integrations/useSyncErrors';
import type {
  SyncError,
  SyncErrorAckFilter,
  SyncErrorsFilters,
} from '@/types/integrations/syncErrors';

const PAGE_SIZE = 25;

export function SyncErrorsView() {
  const { toast } = useToast();
  const [page, setPage] = useState(0);
  const [acknowledged, setAcknowledged] = useState<SyncErrorAckFilter>('false');
  const [phase, setPhase] = useState('');
  const [projectSearch, setProjectSearch] = useState('');

  const filters: SyncErrorsFilters = useMemo(
    () => ({
      acknowledged,
      phase: phase || undefined,
      subhubProjectId: projectSearch || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [acknowledged, phase, projectSearch, page],
  );

  const query = useSyncErrors(filters);
  const ackMutation = useAcknowledgeSyncError();
  const unackMutation = useUnacknowledgeSyncError();

  const rows: SyncError[] = query.data?.data?.data ?? [];
  const total = query.data?.data?.totalCount ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  function handleAck(errorId: string) {
    ackMutation.mutate(errorId, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not acknowledge',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Error acknowledged' });
        }
      },
      onError: () =>
        toast({
          title: 'Could not acknowledge',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }

  function handleUnack(errorId: string) {
    unackMutation.mutate(errorId, {
      onSuccess: (result) => {
        if (result.error) {
          toast({
            title: 'Could not un-acknowledge',
            description: result.error.message,
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Error un-acknowledged' });
        }
      },
      onError: () =>
        toast({
          title: 'Could not un-acknowledge',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sync errors</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Deduplicated SubContractorHub sync errors. Recurring errors collapse
          to one row; the count column shows how many times each has fired since
          first seen. Acknowledge an error to hide it from the default view.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select
              value={acknowledged}
              onValueChange={(v) => {
                setAcknowledged(v as SyncErrorAckFilter);
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="false">Unacknowledged</SelectItem>
                <SelectItem value="true">Acknowledged</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Phase</label>
            <Input
              className="w-[160px]"
              placeholder="e.g. orchestrator"
              value={phase}
              onChange={(e) => {
                setPhase(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Project ID</label>
            <Input
              className="w-[200px]"
              placeholder="subhub project id"
              value={projectSearch}
              onChange={(e) => {
                setProjectSearch(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPhase('');
              setProjectSearch('');
              setAcknowledged('false');
              setPage(0);
            }}
          >
            Clear
          </Button>
        </div>

        {/* Body */}
        {query.isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        {query.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load sync errors</AlertTitle>
            <AlertDescription>
              {(query.error as Error)?.message ?? 'Try refreshing the page.'}
            </AlertDescription>
          </Alert>
        )}

        {!query.isLoading && !query.isError && rows.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No sync errors match the current filters.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">First seen</th>
                  <th className="px-3 py-2 font-medium">Last seen</th>
                  <th className="px-3 py-2 font-medium">Count</th>
                  <th className="px-3 py-2 font-medium">Phase</th>
                  <th className="px-3 py-2 font-medium">Project</th>
                  <th className="px-3 py-2 font-medium">Message</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((row) => {
                  const isAcked = !!row.acknowledgedAt;
                  const isPending =
                    (ackMutation.isPending && ackMutation.variables === row.id) ||
                    (unackMutation.isPending && unackMutation.variables === row.id);
                  return (
                    <tr
                      key={row.id}
                      className={isAcked ? 'text-muted-foreground' : ''}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span title={row.firstSeenAt}>
                          {formatDistanceToNow(new Date(row.firstSeenAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span title={row.lastSeenAt}>
                          {formatDistanceToNow(new Date(row.lastSeenAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={row.count > 1 ? 'default' : 'outline'}>
                          {row.count}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge variant="outline" className="text-[10px]">
                          {row.errorPhase}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 max-w-[160px] truncate">
                        {row.subhubProjectId ?? (
                          <span className="text-muted-foreground italic">
                            (run-level)
                          </span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 max-w-[280px] truncate"
                        title={row.firstMessage}
                      >
                        <span className="font-medium">{row.errorClassName}</span>
                        {': '}
                        <span>{row.firstMessage}</span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isAcked ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs"
                            title={`Acknowledged at ${row.acknowledgedAt}`}
                          >
                            <CheckCircle2 className="h-3 w-3" /> Acked
                          </span>
                        ) : (
                          <span className="text-xs">Open</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {isAcked ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={isPending}
                            onClick={() => handleUnack(row.id)}
                          >
                            {isPending && (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            )}
                            Un-acknowledge
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={isPending}
                            onClick={() => handleAck(row.id)}
                          >
                            {isPending && (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            )}
                            Acknowledge
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-3 w-3" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
